import { FormattedMessage, useIntl } from 'react-intl';
import { DataResult } from 'models/data';
import { getChartColorWithMax } from 'components/chart/data/ChartColors';
import { getLabelForDatapoint } from 'pages/insights-pro/graphs/graphUtils';
import { MeasureComparison } from 'components/MeasureComparison';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { Service } from 'services/data.service';
import { Tile } from 'components/Layout/PageContainer';
import { OverTimeCommonData, OverTimeReport } from 'pages/insights-pro/common/reports/over-time-chart/OverTimeReport';
import { useModelService } from 'services/model/model.service';
import { OverTimeReportInfo } from 'pages/insights-pro/common/reports/over-time-chart/models';
import { OverTimeReportMetrics } from 'pages/insights-pro/common/reports/over-time-chart/OverTimeReportMetrics';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { useEffect } from 'react';
import { OverTimeChart } from '../../../common/reports/over-time-chart/OverTimeChart';
import { MarketplacesOverTimeReportInfoContent } from './MarketplacesOverTimeReportInfoContent';

export function MarketplacesOverTimeReport() {
  const intl = useIntl();

  const { getMeasureByMetric, getAttribute } = useModelService();
  const chnMarketplace = getAttribute('chnMarketplace');
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');

  const { setContent } = useInfoDrawer();

  useEffect(() => {
    const infoContent = {
      titleKey: 'insights.marketplace.reports.over_time',
      subtitleKey: 'info.content.all_dates_utc',
      content: <MarketplacesOverTimeReportInfoContent />,
    };

    setContent(infoContent);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const extractMarketplacesInfo = (data: DataResult) => {
    const marketplaces = data.sets.marketplaces.map(entry => {
      const marketplaceName = chnMarketplace.getValue(entry.attributes);
      return {
        name: marketplaceName,
        totalGmv: ordGmvVal.getValue(entry.values),
        compareGmv: ordGmvVal.getValue(entry.compareValues),
        color: '',
        isOther: false,
        timeInfos: entry.sets.timeSeries.map(timeEntry => ({
          code: getLabelForDatapoint(timeEntry, intl),
          gmv: ordGmvVal.getValue(timeEntry.values),
          comparisonGmv: ordGmvVal.getValue(timeEntry.compareValues),
        })),
        rank: 0,
        compareRank: 0,
      };
    });

    const sortedByCurrent = [...marketplaces]
      .sort((a, b) => b.totalGmv - a.totalGmv)
      .map((marketplace, idx) => ({ ...marketplace, rank: idx + 1 }));

    const sortedByCompare = [...sortedByCurrent]
      .sort((a, b) => b.compareGmv - a.compareGmv)
      .map((marketplace, idx) => ({ ...marketplace, compareRank: idx + 1 }));

    const finalSorted = [...sortedByCompare].sort((a, b) => b.totalGmv - a.totalGmv);

    return finalSorted.map((marketplace, index) => ({
      ...marketplace,
      color: getChartColorWithMax(index, 5),
      isOther: index >= 5,
      rank: index + 1,
    }));
  };

  const extractTotals = (data: DataResult) => ({
    totalGmv: ordGmvVal.getValue(data.values),
    compareGmv: ordGmvVal.getValue(data.compareValues),
  });

  const renderMarketplaceRow = (marketplace: OverTimeReportInfo, commonData: OverTimeCommonData) => {
    const currentVal = marketplace.totalGmv ?? 0;
    const compareVal = marketplace.compareGmv ?? 0;

    const { marketShare, compareShare, growth } = OverTimeReportMetrics(
      currentVal,
      compareVal,
      commonData.totalGmvAllItems,
      commonData.totalCompareGmvAllItems
    );

    return (
      <div key={marketplace.name} className="w-full bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <p className="text-lg font-bold">{marketplace.name}</p>
        </div>
        <div className="grid grid-cols-4 divide-x divide-gray-200 p-4">
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">{ordGmvVal.metric.name}</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">
                {Intl.NumberFormat('en', {
                  notation: 'compact',
                  maximumFractionDigits: 2,
                }).format(currentVal)}
              </span>
              {commonData.isComparisonActive && compareVal !== undefined && (
                <MeasureComparison
                  value={currentVal}
                  comparisonValue={compareVal}
                  unit={MeasureUnitCode.QUANTITY}
                  interpretation={1}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.market_share" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">{marketShare}%</span>
              {commonData.isComparisonActive && (
                <MeasureComparison
                  value={parseFloat(marketShare)}
                  comparisonValue={parseFloat(compareShare)}
                  unit={MeasureUnitCode.QUANTITY}
                  interpretation={1}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.growth" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">{growth}%</span>
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.marketplace_ranking" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">#{marketplace.rank}</span>
              {commonData.isComparisonActive && (
                <MeasureComparison
                  value={marketplace.compareRank}
                  comparisonValue={marketplace.rank}
                  unit={MeasureUnitCode.COUNT}
                  interpretation={1}
                />
              )}
            </div>
          </div>
        </div>
        <Tile
          title={ordGmvVal.metric.name}
          additionalTitle={intl.formatMessage({
            id: 'general.title.over_time',
          })}
        >
          <OverTimeChart overTimeReport={marketplace} timeInfos={commonData.timeInfos} measure={ordGmvVal} />
        </Tile>
      </div>
    );
  };

  return (
    <OverTimeReport
      service={Service.Marketplaces}
      extractItems={extractMarketplacesInfo}
      extractTotals={extractTotals}
      titleId="insights.marketplace.reports.over_time"
      descriptionId="insights.marketplace.reports.over_time.description"
      testId="marketplaces-over-time-chart"
      additionalTitle={intl.formatMessage(
        {
          id: 'general.title.by-metric',
        },
        {
          metric: ordGmvVal.metric.name,
        }
      )}
      renderRow={renderMarketplaceRow}
    />
  );
}
